import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { ChevronLeft, ChevronRight } from '@kula-ai/icons-core';
import { useChatSubmit } from '../../../hooks';
import type { SuggestedAction } from '../../../types';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { CandidateProfileCard } from './CandidateProfileCard';
import type { CandidateProfilesBlockProps } from './CandidateProfilesBlock.types';

const ArrowButton = ({
  dir,
  disabled,
  onClick,
}: {
  dir: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) => (
  <Flex
    as="button"
    type="button"
    aria-label={dir === 'prev' ? 'Previous candidate' : 'Next candidate'}
    onClick={onClick}
    disabled={disabled}
    align="center"
    justify="center"
    flexShrink={0}
    w="32px"
    h="32px"
    borderRadius="full"
    border="1px solid"
    borderColor="secondary-200"
    bg="base-white"
    color="secondary-600"
    boxShadow="0px 2px 6px rgba(38, 34, 68, 0.08)"
    opacity={disabled ? 0.4 : 1}
    cursor={disabled ? 'not-allowed' : 'pointer'}
    _hover={
      disabled
        ? undefined
        : { borderColor: 'primary-300', color: 'primary-600' }
    }
  >
    {dir === 'prev' ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
  </Flex>
);

const SuggestedActions = ({ actions }: { actions: SuggestedAction[] }) => {
  const { send, isSending } = useChatSubmit();
  return (
    <Box mt="14px">
      <SuggestedActionChips
        actions={actions}
        onSelect={(prompt) => void send(prompt)}
        disabled={isSending}
        testIdPrefix="agentforge-profile-suggested"
      />
    </Box>
  );
};

// Faint cards peeking behind the active one to signal more candidates.
const DepthStack = ({ remaining }: { remaining: number }) => {
  if (remaining <= 0) return null;
  const layers = Math.min(remaining, 2);
  return (
    <>
      {Array.from({ length: layers }).map((_, i) => {
        const depth = layers - i;
        return (
          <Box
            key={depth}
            position="absolute"
            inset="0"
            transform={`translate(${depth * 7}px, ${depth * 7}px)`}
            bg="base-white"
            border="1px solid"
            borderColor="secondary-100"
            borderRadius="12px"
            boxShadow="0px 6px 16px rgba(38, 34, 68, 0.05)"
            zIndex={0}
          />
        );
      })}
    </>
  );
};

export const CandidateProfilesBlock = ({
  data,
}: CandidateProfilesBlockProps) => {
  const [index, setIndex] = useState(0);
  const candidates = data.candidates;
  if (candidates.length === 0) return null;

  const safeIndex = Math.min(index, candidates.length - 1);
  const current = candidates[safeIndex];
  const total = candidates.length;
  const isMulti = total > 1;

  return (
    <Box data-test-id="agentforge-candidate-profiles-block">
      {data.intro && (
        <Box mb="12px">
          <Text variant="p" as="span" className="text-secondary-700">
            {data.intro}
          </Text>
        </Box>
      )}

      <Flex align="center" gap="10px">
        {isMulti && (
          <ArrowButton
            dir="prev"
            disabled={safeIndex === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          />
        )}

        <Box position="relative" flex="1" minW="0">
          <DepthStack remaining={total - safeIndex - 1} />
          <Box position="relative" zIndex={1}>
            <CandidateProfileCard key={current.id} candidate={current} active />
          </Box>
        </Box>

        {isMulti && (
          <ArrowButton
            dir="next"
            disabled={safeIndex >= total - 1}
            onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
          />
        )}
      </Flex>

      {isMulti && (
        <Flex justify="center" mt="10px">
          <Text variant="p" as="span" className="text-secondary-400">
            {safeIndex + 1} of {total}
          </Text>
        </Flex>
      )}

      {data.suggestedActions && data.suggestedActions.length > 0 && (
        <SuggestedActions actions={data.suggestedActions} />
      )}
    </Box>
  );
};
